import { EventBus } from "@/event-bus";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  namespaced: true,
  state: {
    uploads: [],
  },
  mutations: {
    ADD_UPLOAD(state, upload) {
      state.uploads.push({
        ...upload,
        progress: 0,
        speed: 0,
        paused: false,
        failed: false,
        completed: false,
        cancelTokenSource: null,
        currentChunkIndex: 0,
      });
    },
    REMOVE_UPLOAD(state, sessionId) {
      state.uploads = state.uploads.filter((u) => u.sessionId !== sessionId);
    },
    UPDATE_UPLOAD_PROGRESS(
      state,
      { sessionId, progress, speed, currentChunkIndex }
    ) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.progress = progress;
        upload.speed = speed;
        upload.failed = false;
        upload.processing = false;
        if (typeof currentChunkIndex === "number") {
          upload.currentChunkIndex = currentChunkIndex;
        }
      }
    },
    SET_UPLOAD_PAUSED(state, { sessionId, paused }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.paused = paused;
      }
    },
    SET_UPLOAD_PROCESSING(state, { sessionId, processing }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.processing = processing;
      }
    },
    SET_UPLOAD_COMPLETED(state, { sessionId, completed }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.completed = completed;
        upload.failed = false;
        upload.processing = false;
      }
    },
    SET_UPLOAD_FAILED(state, { sessionId, failed }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.failed = failed;
      }
    },
    SET_UPLOAD_CANCEL_TOKEN(state, { sessionId, cancelTokenSource }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.cancelTokenSource = cancelTokenSource;
      }
    },
    SET_UPLOAD_MINT(state, { sessionId, mintData }) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload) {
        upload.mintData = mintData;
      }
    },
  },
  actions: {
    addUpload({ commit }, payload) {
      const totalChunks = Math.ceil(payload.file.size / payload.chunkSize);
      commit("ADD_UPLOAD", {
        ...payload,
        totalChunks,
      });
    },

    pauseUpload({ commit }, sessionId) {
      commit("SET_UPLOAD_PAUSED", { sessionId, paused: true });
    },

    resumeUpload({ commit, dispatch }, sessionId) {
      commit("SET_UPLOAD_PAUSED", { sessionId, paused: false });
      dispatch("startUpload", sessionId);
    },

    cancelUpload({ state, commit }, sessionId) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (upload && upload.cancelTokenSource) {
        upload.cancelTokenSource.cancel("Upload canceled by user");
      }
      commit("REMOVE_UPLOAD", sessionId);
    },

    async startUpload({ state, commit, dispatch }, sessionId) {
      const upload = state.uploads.find((u) => u.sessionId === sessionId);
      if (!upload) {
        return;
      }
      try {
        // Step 1: Mint signed URL (creates engagement note + pending encounter)
        let mintData = upload.mintData;
        if (!mintData) {
          const { data } = await this.$http.post(
            `patients/${upload.userId}/scribe/upload-url`,
            {
              filename: upload.fileName,
              content_type: upload.file.type || "audio/mpeg",
              content_length: upload.file.size,
              recording_language: upload.language,
              title: upload.title,
              report: upload.report,
              approved: upload.approved,
              appointment_id: upload.appointment_id,
              group_session_id: upload.group_session_id,
              note_type: upload.note_type,
            }
          );
          mintData = data;
          commit("SET_UPLOAD_MINT", { sessionId, mintData });
        }
        // Step 2: PUT file directly to GCS
        commit("UPDATE_UPLOAD_PROGRESS", { sessionId, progress: 5, speed: 0 });
        await dispatch("putFileToGcs", {
          sessionId,
          file: upload.file,
          uploadUrl: mintData.upload_url,
          requiredHeaders: mintData.required_headers,
        });
        commit("SET_UPLOAD_PROCESSING", { sessionId, processing: true });
        await this.$http.post(
          `patients/${upload.userId}/scribe/${mintData.encounter_id}/finalize`
        );
        commit("SET_UPLOAD_COMPLETED", { sessionId, completed: true });
        EventBus.$emit("upload-scribe-complete", { sessionId });
        Swal.fire({
          icon: "success",
          title: "Upload Completed",
          text: `File "${upload.file.name}" uploaded successfully.`,
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          commit("REMOVE_UPLOAD", sessionId);
          return;
        }
        if (error?.response?.data?.code === "OBJECT_NOT_FOUND") {
          commit("SET_UPLOAD_MINT", { sessionId, mintData: null });
        }
        commit("SET_UPLOAD_FAILED", { sessionId, failed: true });
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text:
            error?.response?.data?.message ||
            error?.data?.message ||
            error?.message ||
            "An error occurred during upload.",
        });
      }
    },
    putFileToGcs({ commit }, { sessionId, file, uploadUrl, requiredHeaders }) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", uploadUrl);
        Object.entries(requiredHeaders || {}).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 90) + 5;
            commit("UPDATE_UPLOAD_PROGRESS", { sessionId, progress, speed: 0 });
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            commit("UPDATE_UPLOAD_PROGRESS", {
              sessionId,
              progress: 95,
              speed: 0,
            });
            resolve();
          } else {
            reject(
              new Error(`GCS PUT failed: ${xhr.status} ${xhr.responseText}`)
            );
          }
        };
        xhr.onerror = () => reject(new Error("GCS PUT network error"));
        xhr.ontimeout = () => reject(new Error("GCS PUT timed out"));
        xhr.send(file);
      });
    },
    retryUpload({ dispatch, commit }, sessionId) {
      commit("SET_UPLOAD_FAILED", { sessionId, failed: false });
      dispatch("startUpload", sessionId);
    },
  },
};
